import type { ReactNode } from "react";
import { SkyText } from "@skypointcloud/skypoint-ui";
import { needAttentionSentence } from "@shared/types/monitor";
import { OverviewCard, OverviewLink, statusTone } from "@/components/overview/OverviewCard";
import { useMonitorSummary } from "@/hooks/use-monitor";
import { SkyMonitorIcon } from "@/lib/icons";
import { ROUTES } from "@/lib/routes";

/**
 * The Overview's Data health tile (spec section 11): "Data health: {n} tables need attention",
 * and (round 6 W15) the schedules too, so it agrees with skyMonitor's Incidents tab, linking to
 * the skyMonitor page. Shown only with `features.flags.monitor` on and `monitor.read`;
 * a failed read says so in the tile and leaves the other cards alone.
 */
export function DataHealthCard() {
  const summary = useMonitorSummary(true);
  function renderSummary(): ReactNode {
    if (summary.isPending) {
      return (
        <SkyText size={300} color="muted" block>
          Loading
        </SkyText>
      );
    }
    if (summary.isError || !summary.data) {
      return (
        <SkyText size={300} color="muted" block testId="overview-data-health-error">
          Data health could not be read.
        </SkyText>
      );
    }
    if (summary.data.tables.watched === 0) {
      return (
        <SkyText size={300} block testId="overview-data-health-empty">
          No table has loaded yet, so there is nothing to watch.
        </SkyText>
      );
    }
    return (
      <SkyText
        size={300}
        weight={summary.data.needAttention > 0 ? "semibold" : undefined}
        block
        testId="overview-data-health-sentence"
      >
        {needAttentionSentence(summary.data.attention.tables, summary.data.attention.schedules)}
      </SkyText>
    );
  }

  return (
    <OverviewCard
      id="data-health"
      // Owner decision O6 (round 10 wave 2): the product name leads, as in the sidebar.
      title="skyMonitor · Data health"
      icon={SkyMonitorIcon}
      tone={
        summary.data && summary.data.tables.watched > 0
          ? statusTone({ failed: 0, warning: summary.data.needAttention, ok: 1 })
          : "info"
      }
      testId="overview-data-health"
      action={<OverviewLink to={ROUTES.monitor}>Open skyMonitor</OverviewLink>}
    >
      {renderSummary()}
    </OverviewCard>
  );
}
